import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mirror `user.user_id` to a localStorage key so `metaPixel.js` can
  // include it as `external_id` on every CAPI event — Meta uses that
  // for cross-device attribution AND it satisfies the "AddToCart sent
  // without user_data" Diagnostics warning for logged-in shoppers.
  // Sept 2026 — see `frontend/src/lib/metaPixel.js::collectUserIdentifiers`.
  const persistExternalId = useCallback((u) => {
    try {
      if (u?.user_id) localStorage.setItem("tbb_user_id", u.user_id);
      else if (u?.id) localStorage.setItem("tbb_user_id", u.id);
      else localStorage.removeItem("tbb_user_id");
      // Also opportunistically stash email/name so Meta events fired
      // from any page (not just after the shopper fills the cart form)
      // still carry the hashed match keys.
      if (u?.email) localStorage.setItem("tbb_capture_email", u.email.toLowerCase());
      if (u?.name) localStorage.setItem("tbb_capture_name", u.name);
      if (u?.mobile) localStorage.setItem("tbb_capture_phone", u.mobile);
    } catch { /* localStorage disabled — fine */ }
  }, []);

  const applyUser = useCallback((u) => {
    setUser(u);
    persistExternalId(u);
  }, [persistExternalId]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
    } catch {
      applyUser(null);
    } finally {
      setLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    // If returning from Emergent OAuth (session_id in hash), let callback route handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applyUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    applyUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: applyUser, loading, login, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
